import { useEffect, useState } from "react";
import { api, fmt, formatErr, downloadPDF, whatsappShare } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileText, Share2 } from "lucide-react";
import { PageHeader } from "@/components/EntityPage";

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ kind: "invoice", tax_percent: 5, amount: 0 });

  async function load() {
    const [inv, c] = await Promise.all([api.get("/invoices"), api.get("/clients")]);
    setRows(inv.data); setClients(c.data);
  }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    try {
      const client = clients.find((x) => x.id === f.client_id);
      await api.post("/invoices", { ...f, client_name: client?.name || f.client_name || "Guest", amount: parseFloat(f.amount) || 0, tax_percent: parseFloat(f.tax_percent) || 0 });
      toast.success("Created");
      setOpen(false); setF({ kind: "invoice", tax_percent: 5, amount: 0 }); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  function printOne(r) {
    downloadPDF(`${r.kind.toUpperCase()} · ${r.number}`, [{
      Number: r.number, Client: r.client_name, Kind: r.kind,
      Amount: r.amount, "Tax %": r.tax_percent, "Tax Amount": r.tax_amount,
      Total: r.total, Date: r.created_at?.slice(0, 10),
    }]);
  }

  return (
    <div>
      <PageHeader
        title="Invoices & Receipts" subtitle="Generate, download and share invoices/receipts on WhatsApp."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="inv-add" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4"/> New</Button>
            </DialogTrigger>
            <DialogContent className="bg-[hsl(224_45%_9%)] border-border">
              <DialogHeader><DialogTitle className="font-display text-2xl">New Invoice / Receipt</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] uppercase text-muted-foreground">Kind</Label>
                    <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                      <SelectTrigger className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
                      <SelectContent><SelectItem value="invoice">Invoice</SelectItem><SelectItem value="receipt">Receipt</SelectItem></SelectContent>
                    </Select></div>
                  <div><Label className="text-[10px] uppercase text-muted-foreground">Client</Label>
                    <Select value={f.client_id || ""} onValueChange={(v) => setF({ ...f, client_id: v })}>
                      <SelectTrigger className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Select client"/></SelectTrigger>
                      <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-[10px] uppercase text-muted-foreground">Amount ₹</Label>
                    <Input required type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="inv-amount" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                  <div><Label className="text-[10px] uppercase text-muted-foreground">Tax %</Label>
                    <Input type="number" value={f.tax_percent} onChange={(e) => setF({ ...f, tax_percent: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                </div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Notes</Label>
                  <Input value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div className="flex justify-end"><Button type="submit" data-testid="inv-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Create</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <Card className="bg-[hsl(224_45%_9%)] border-border">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
            {["Number","Kind","Client","Amount","Tax","Total","Date","Actions"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No invoices yet</td></tr> :
              rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-mono text-amber-300">{r.number}</td>
                  <td className="p-3 uppercase text-xs">{r.kind}</td>
                  <td className="p-3">{r.client_name}</td>
                  <td className="p-3 font-mono">{fmt(r.amount)}</td>
                  <td className="p-3 font-mono text-muted-foreground">{fmt(r.tax_amount)}</td>
                  <td className="p-3 font-mono text-emerald-300">{fmt(r.total)}</td>
                  <td className="p-3 text-xs">{r.created_at?.slice(0, 10)}</td>
                  <td className="p-3">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" data-testid={`inv-pdf-${r.id}`} className="h-8 w-8" onClick={() => printOne(r)}><FileText className="w-3.5 h-3.5"/></Button>
                      <Button variant="ghost" size="icon" data-testid={`inv-wa-${r.id}`} className="h-8 w-8 text-emerald-400" onClick={() => whatsappShare(`${r.kind.toUpperCase()} ${r.number} · ${r.client_name} · Total ₹${r.total}`)}><Share2 className="w-3.5 h-3.5"/></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
